import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faWandMagicSparkles,
  faRotate,
  faChartSimple,
  faChevronDown,
  faChevronRight,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import clsx from "clsx";
import { CC_ACCENT_LINK, CC_GHOST_BTN, CC_PANEL, CC_PANEL_FLUSH, CC_TABLE_HEAD } from "@/components/case-checking/caseCheckingChrome";
import { Button } from "@/components/ui/button/Button";
import { InsightsFilterBar } from "@/components/case-checking/InsightsFilterBar";
import { Switch } from "@/components/ui/switch/Switch";
import {
  getPortfolioAssessment,
  getPortfolioInsights,
  type PortfolioAssessment,
  type PortfolioInsights,
  type PortfolioRag,
  type InsightTrend,
} from "@/services/api/case-checking";

export interface InsightsCaseRef {
  id: string;
  adviser: string;
  caseType: string;
}

interface PortfolioInsightsViewProps {
  cases: InsightsCaseRef[];
}

const CASE_TYPE_LABELS: Record<string, string> = {
  "annual-review": "Annual Review (no change)",
  "fund-switch": "Fund Switch",
  "top-up": "Top Up",
  "isa-gia-withdrawal": "ISA/GIA Withdrawal",
  "bond-withdrawal": "Bond Withdrawal",
  "pension-withdrawal": "Ad hoc Pension Withdrawal",
  "platform-transfer": "Platform Transfer",
  "new-business": "New Business",
  "first-fad-drawdown": "First-time FAD Drawdown",
  "br-vct-seis": "BR/VCT/SEIS",
  "new-trust": "New Trust",
  "existing-trust": "Existing Trust",
  mortgage: "Mortgage",
  annuity: "Annuity",
  other: "Other",
};

function caseTypeLabel(slug: string): string {
  return CASE_TYPE_LABELS[slug] ?? slug.replace(/-/g, " ");
}

const RAG_CELL: Record<PortfolioRag, string> = {
  red: "bg-red-100 text-red-800 ring-red-200 dark:bg-red-500/20 dark:text-red-300 dark:ring-red-500/30",
  amber: "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/25",
  green: "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/25",
  grey: "bg-slate-100 dark:bg-white/5 text-slate-500 ring-1 ring-slate-200 dark:ring-white/10",
};

const SEVERITY_DOT: Record<InsightTrend["severity"], string> = {
  critical: "bg-red-400",
  warning: "bg-amber-400",
  info: "bg-sky-400",
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function trendInsight(t: InsightTrend): string {
  return t.insight?.trim() || t.detail?.trim() || "";
}

/** Cross-case insights: headline stats, trends, top failures; grid on demand. */
export function PortfolioInsightsView({ cases }: PortfolioInsightsViewProps) {
  const [adviserFilter, setAdviserFilter] = useState("");
  const [caseTypeFilter, setCaseTypeFilter] = useState("");
  const [assessment, setAssessment] = useState<PortfolioAssessment | null>(null);
  const [insights, setInsights] = useState<PortfolioInsights | null>(null);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [onlyFailures, setOnlyFailures] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const adviserOptions = useMemo(() => {
    const names = new Set<string>();
    for (const c of cases) {
      const name = c.adviser.trim();
      if (name) names.add(name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [cases]);

  const caseTypeOptions = useMemo(() => {
    const types = new Set<string>();
    for (const c of cases) {
      if (c.caseType) types.add(c.caseType);
    }
    return Array.from(types).sort((a, b) =>
      caseTypeLabel(a).localeCompare(caseTypeLabel(b)),
    );
  }, [cases]);

  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      if (adviserFilter && c.adviser !== adviserFilter) return false;
      if (caseTypeFilter && c.caseType !== caseTypeFilter) return false;
      return true;
    });
  }, [cases, adviserFilter, caseTypeFilter]);

  const hasActiveFilter = Boolean(adviserFilter || caseTypeFilter);

  const caseIds = useMemo((): string[] | undefined => {
    if (!hasActiveFilter) return undefined;
    return filteredCases.map((c) => c.id);
  }, [filteredCases, hasActiveFilter]);

  const caseIdsKey = caseIds?.join(",") ?? "all";

  const loadGrid = useCallback(async () => {
    if (hasActiveFilter && filteredCases.length === 0) {
      setAssessment(null);
      return;
    }
    setLoadingGrid(true);
    setError(null);
    try {
      setAssessment(await getPortfolioAssessment(caseIds));
    } catch (err) {
      console.error("Failed to load portfolio assessment:", err);
      setError("Could not load assessment data.");
    } finally {
      setLoadingGrid(false);
    }
  }, [caseIds, filteredCases.length, hasActiveFilter]);

  const loadInsights = useCallback(
    async (refresh: boolean) => {
      if (hasActiveFilter && filteredCases.length === 0) {
        setInsights(null);
        return;
      }
      setLoadingInsights(true);
      try {
        setInsights(await getPortfolioInsights(caseIds, refresh));
      } catch (err) {
        console.error("Failed to load portfolio insights:", err);
      } finally {
        setLoadingInsights(false);
      }
    },
    [caseIds, filteredCases.length, hasActiveFilter],
  );

  const refreshAll = useCallback(() => {
    void loadGrid();
    void loadInsights(true);
  }, [loadGrid, loadInsights]);

  const clearFilters = useCallback(() => {
    setAdviserFilter("");
    setCaseTypeFilter("");
  }, []);

  useEffect(() => {
    void loadGrid();
    void loadInsights(false);
  }, [caseIdsKey, loadGrid, loadInsights]);

  const totals = assessment?.totals;
  const mixedVersions = (assessment?.rule_set_versions.length ?? 0) > 1;

  const visibleRows = useMemo(() => {
    if (!assessment) return [];
    return onlyFailures ? assessment.rows.filter((r) => r.fail_count > 0) : assessment.rows;
  }, [assessment, onlyFailures]);

  const topFailures = assessment?.consistent_failures.slice(0, 8) ?? [];

  if (cases.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <FontAwesomeIcon icon={faChartSimple} className="w-8 h-8 mb-3 opacity-50" />
        <p className="text-sm">No cases yet. Insights appear once cases are assessed.</p>
      </div>
    );
  }

  if (hasActiveFilter && filteredCases.length === 0) {
    return (
      <div className="space-y-5">
        <InsightsHeader
          assessment={null}
          mixedVersions={false}
          filterSummary={buildFilterSummary(adviserFilter, caseTypeFilter, 0)}
          onRefresh={() => {}}
          refreshing={false}
        />
        <InsightsFilters
          adviserFilter={adviserFilter}
          caseTypeFilter={caseTypeFilter}
          adviserOptions={adviserOptions}
          caseTypeOptions={caseTypeOptions}
          onAdviserChange={setAdviserFilter}
          onCaseTypeChange={setCaseTypeFilter}
          onClear={clearFilters}
          hasActiveFilter={hasActiveFilter}
        />
        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
          <p className="text-sm">No cases match these filters.</p>
          <Button
            plain size="unstyled"
            type="button"
            onClick={clearFilters}
            className={clsx(CC_ACCENT_LINK, "px-0 mt-2 text-xs")}
          >
            Clear filters
          </Button>
        </div>
      </div>
    );
  }

  const refreshing = loadingGrid || loadingInsights;
  const filterSummary = buildFilterSummary(
    adviserFilter,
    caseTypeFilter,
    assessment?.totals.cases ?? filteredCases.length,
  );

  return (
    <div className="space-y-5">
      <InsightsHeader
        assessment={assessment}
        mixedVersions={mixedVersions}
        filterSummary={filterSummary}
        onRefresh={refreshAll}
        refreshing={refreshing}
      />

      <InsightsFilters
        adviserFilter={adviserFilter}
        caseTypeFilter={caseTypeFilter}
        adviserOptions={adviserOptions}
        caseTypeOptions={caseTypeOptions}
        onAdviserChange={setAdviserFilter}
        onCaseTypeChange={setCaseTypeFilter}
        onClear={clearFilters}
        hasActiveFilter={hasActiveFilter}
      />
      {error ? (
        <div className="rounded-lg bg-red-500/10 text-red-300 px-4 py-3 text-sm ring-1 ring-red-500/20">
          {error}
        </div>
      ) : null}

      {refreshing ? (
        <div
          className={clsx(CC_PANEL, "py-2.5 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300")}
          data-testid="insights-recalculating"
        >
          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-indigo-400 animate-spin shrink-0" />
          Recalculating insights for {filterSummary}…
        </div>
      ) : null}

      <div
        className={clsx(
          "space-y-5 transition-opacity",
          refreshing && "opacity-50 pointer-events-none",
        )}
      >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <StatChip label="Cases" value={totals?.cases ?? 0} />
        <StatChip label="Pass" value={totals?.passed ?? 0} tone="emerald" />
        <StatChip label="Unclear" value={totals?.unclear ?? 0} tone="amber" />
        <StatChip label="Fail" value={totals?.failed ?? 0} tone="red" />
      </div>

      {assessment && assessment.group_breakdown.some((g) => g.failed + g.unclear > 0) ? (
        <CategoryBreakdown breakdown={assessment.group_breakdown} />
      ) : null}

      <div className={CC_PANEL}>
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faWandMagicSparkles} className="w-4 h-4 text-indigo-400" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Trends</h3>
          {insights?.generated_at && !loadingInsights ? (
            <span className="text-xs text-slate-500">{relativeTime(insights.generated_at)}</span>
          ) : null}
        </div>

        {loadingInsights && !insights ? (
          <p className="text-sm text-slate-400 flex items-center gap-2 py-4">
            <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
            Generating trends…
          </p>
        ) : insights ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{insights.headline}</p>
            {insights.trends.length > 0 ? (
              <ul className="space-y-2.5">
                {insights.trends.map((t, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span
                      className={clsx("mt-1.5 h-1.5 w-1.5 rounded-full shrink-0", SEVERITY_DOT[t.severity])}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{t.title}</p>
                      <p className="text-sm text-slate-400 mt-0.5 leading-relaxed">{trendInsight(t)}</p>
                      {t.stat ? <p className="text-xs text-slate-500 mt-1">{t.stat}</p> : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No trends yet.</p>
        )}
      </div>

      <div className={CC_PANEL}>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Most common failures</h3>
        {topFailures.length > 0 ? (
          <ul className="space-y-2">
            {topFailures.map((f) => (
              <li
                key={f.rule_id}
                className="flex items-start justify-between gap-3 text-sm border-b border-slate-200 dark:border-white/5 last:border-0 pb-2 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="text-slate-700 dark:text-slate-200 font-medium truncate">{f.label}</p>
                  <p className="text-xs text-slate-500 truncate">{f.group_name}</p>
                </div>
                <div className="shrink-0 text-right text-xs tabular-nums">
                  <span className="text-red-400 font-semibold">{f.cases_failed}</span>
                  <span className="text-slate-500">/{f.applicable_count} cases</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No consistent failures across cases.</p>
        )}
      </div>

      <div className={CC_PANEL_FLUSH}>
        <Button
          plain
          type="button"
          onClick={() => setShowGrid((v) => !v)}
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
        >
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Full rule grid</span>
          <FontAwesomeIcon
            icon={showGrid ? faChevronDown : faChevronRight}
            className="w-3.5 h-3.5 text-slate-500"
          />
        </Button>

        {showGrid ? (
          <div className="border-t border-slate-200 dark:border-white/5 px-4 pb-4">
            <div className="flex items-center justify-between py-3">
              <p className="text-xs text-slate-500">Rule × case matrix</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">Failures only</span>
                <Switch checked={onlyFailures} onChange={setOnlyFailures}>
                  <span className="sr-only">Failures only</span>
                </Switch>
              </div>
            </div>

            {loadingGrid && !assessment ? (
              <p className="text-sm text-slate-400 flex items-center gap-2 py-6">
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Loading grid…
              </p>
            ) : assessment ? (
              <div className="overflow-x-auto max-h-[50vh] overflow-y-auto rounded-lg ring-1 ring-slate-200 dark:ring-white/5">
                <table className="text-xs border-separate border-spacing-0 w-full">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className={clsx("sticky left-0 z-20 text-left text-slate-400 font-medium px-3 py-2 min-w-[200px] border-b border-slate-200 dark:border-white/5", CC_TABLE_HEAD)}>
                        Rule
                      </th>
                      {assessment.columns.map((col) => (
                        <th
                          key={col.case_id}
                          className={clsx(CC_TABLE_HEAD, "text-slate-400 font-medium px-2 py-2 border-b border-slate-200 dark:border-white/5 whitespace-nowrap")}
                          title={col.client_name}
                        >
                          {col.client_name.length > 10
                            ? `${col.client_name.slice(0, 9)}…`
                            : col.client_name}
                        </th>
                      ))}
                      <th className={clsx(CC_TABLE_HEAD, "text-slate-400 font-medium px-2 py-2 border-b border-slate-200 dark:border-white/5")}>
                        Fail
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <tr key={row.rule_id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                        <td className="sticky left-0 z-10 bg-white dark:bg-slate-900/90 px-3 py-1.5 border-b border-slate-200 dark:border-white/5">
                          <span className="text-slate-700 dark:text-slate-200">{row.label}</span>
                          <span className="block text-[10px] text-slate-500">{row.group_name}</span>
                        </td>
                        {row.cells.map((cell) => (
                          <td key={cell.case_id} className="px-1 py-1 border-b border-slate-200 dark:border-white/5 text-center">
                            <span
                              className={clsx(
                                "inline-block min-w-[40px] rounded px-1 py-0.5 text-[10px] capitalize",
                                cell.rag ? RAG_CELL[cell.rag] : "text-slate-600",
                              )}
                              title={cell.status ?? "not in set"}
                            >
                              {cell.status ?? "—"}
                            </span>
                          </td>
                        ))}
                        <td className="px-2 py-1 border-b border-slate-200 dark:border-white/5 text-center text-red-400 font-medium tabular-nums">
                          {row.fail_count || ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      </div>
    </div>
  );
}

function buildFilterSummary(
  adviser: string,
  caseType: string,
  caseCount: number,
): string {
  const parts: string[] = [`${caseCount} case${caseCount === 1 ? "" : "s"}`];
  if (adviser) parts.push(adviser);
  if (caseType) parts.push(caseTypeLabel(caseType));
  return parts.join(" · ");
}

function InsightsHeader({
  assessment,
  mixedVersions,
  filterSummary,
  onRefresh,
  refreshing,
}: {
  assessment: PortfolioAssessment | null;
  mixedVersions: boolean;
  filterSummary: string;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Insights</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          {refreshing ? (
            "Updating stats and trends…"
          ) : assessment ? (
            <>
              {filterSummary}
              {mixedVersions ? (
                <span className="ml-2 text-amber-400/90">· mixed rule-set versions</span>
              ) : null}
            </>
          ) : (
            "Loading…"
          )}
        </p>
      </div>
      <Button
        plain size="unstyled"
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className={clsx(CC_GHOST_BTN, "py-2 disabled:opacity-50")}
      >
        <FontAwesomeIcon
          icon={refreshing ? faSpinner : faRotate}
          className={clsx("w-3.5 h-3.5", refreshing && "animate-spin")}
        />
        {refreshing ? "Refreshing…" : "Refresh"}
      </Button>
    </div>
  );
}

function InsightsFilters({
  adviserFilter,
  caseTypeFilter,
  adviserOptions,
  caseTypeOptions,
  onAdviserChange,
  onCaseTypeChange,
  onClear,
  hasActiveFilter,
}: {
  adviserFilter: string;
  caseTypeFilter: string;
  adviserOptions: string[];
  caseTypeOptions: string[];
  onAdviserChange: (value: string) => void;
  onCaseTypeChange: (value: string) => void;
  onClear: () => void;
  hasActiveFilter: boolean;
}) {
  return (
    <InsightsFilterBar
      adviserFilter={adviserFilter}
      caseTypeFilter={caseTypeFilter}
      adviserOptions={adviserOptions}
      caseTypeOptions={caseTypeOptions}
      formatCaseTypeLabel={caseTypeLabel}
      onAdviserChange={onAdviserChange}
      onCaseTypeChange={onCaseTypeChange}
      onClear={onClear}
      hasActiveFilter={hasActiveFilter}
    />
  );
}

function StatChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "emerald" | "amber" | "red";
}) {
  const valueTone =
    tone === "emerald"
      ? "text-emerald-400"
      : tone === "amber"
        ? "text-amber-400"
        : tone === "red"
          ? "text-red-400"
          : "text-slate-900 dark:text-slate-100";

  return (
    <div className={clsx(CC_PANEL, "py-3")}>
      <p className={clsx("text-2xl font-semibold tabular-nums", valueTone)}>{value}</p>
      <p className="text-xs text-slate-400 mt-0.5">{label}</p>
    </div>
  );
}

function CategoryBreakdown({
  breakdown,
}: {
  breakdown: PortfolioAssessment["group_breakdown"];
}) {
  const max = Math.max(1, ...breakdown.map((g) => g.failed + g.unclear));

  return (
    <div className={CC_PANEL}>
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Failures by category</h3>
      <div className="space-y-2">
        {breakdown.map((g) => {
          const total = g.failed + g.unclear;
          if (total === 0) return null;
          return (
            <div key={g.group_name} className="flex items-center gap-3">
              <span className="w-36 shrink-0 text-xs text-slate-400 truncate" title={g.group_name}>
                {g.group_name}
              </span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-100 dark:bg-white/5">
                <div
                  className="h-full rounded-full bg-red-400/70"
                  style={{ width: `${(total / max) * 100}%` }}
                />
              </div>
              <span className="w-12 shrink-0 text-right text-xs text-slate-400 tabular-nums">
                {total}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
